import { SingleDayHoliday, MultiDayHoliday } from '../types/holiday';
import { GermanState } from '../types/GermanState';
import { holidays } from '../data/holidays';
import { parseDateString } from '../utils/dateUtils';
import { PLAN_YEAR } from '../constants/planYear';

const isValidDate = (date: any): date is Date => {
  return date instanceof Date && !isNaN(date.getTime());
};

const capitalizeHolidayName = (name: string): string => {
  // Split by spaces and capitalize each word
  return name.split(' ').map(word => {
    // Special handling for state abbreviations and specific words
    if (word === 'baden-württemberg') return 'Baden-Württemberg';
    if (word === 'nordrhein-westfalen') return 'Nordrhein-Westfalen';
    if (word === 'rheinland-pfalz') return 'Rheinland-Pfalz';
    if (word === 'sachsen-anhalt') return 'Sachsen-Anhalt';
    if (word === 'mecklenburg-vorpommern') return 'Mecklenburg-Vorpommern';
    if (word === '(beweglicher') return '(Beweglicher';
    if (word === 'ferientag)') return 'Ferientag)';
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
};

const getHolidaysForYear = (year: number, stateCode: GermanState) => {
  const allHolidays = holidays.publicHolidays[year]?.ALL || [];
  const stateHolidays = holidays.publicHolidays[year]?.[stateCode] || [];
  
  return {
    school: holidays.schoolHolidays[year]?.[stateCode] || [],
    public: [
      ...allHolidays.map(h => ({ ...h, state: stateCode })),
      ...stateHolidays.map(h => ({ ...h, state: stateCode }))
    ]
  };
};

export const holidayService = {
  async getSchoolHolidays(
    stateCode: GermanState | null,
    year: number = PLAN_YEAR
  ): Promise<MultiDayHoliday[]> {
    if (!stateCode) return [];
    
    try {
      const yearHolidays = getHolidaysForYear(year, stateCode).school;
      
      return yearHolidays
        .map(holiday => {
          const start = parseDateString(holiday.start);
          const end = holiday.end ? parseDateString(holiday.end) : start;
          
          if (!isValidDate(start) || !isValidDate(end)) {
            console.error('Invalid school holiday dates:', holiday);
            return null;
          }
          
          const holidayObj: MultiDayHoliday = {
            name: capitalizeHolidayName(holiday.name),
            start: holiday.start,
            end: holiday.end || holiday.start,
            date: holiday.start,
            endDate: holiday.end || holiday.start,
            type: 'school',
            state: stateCode
          };
          
          return holidayObj;
        })
        .filter((h): h is MultiDayHoliday => h !== null);
    } catch (error) {
      console.error('Error getting school holidays:', error);
      return [];
    }
  },

  async getPublicHolidays(
    stateCode: GermanState | null,
    year: number = PLAN_YEAR
  ): Promise<SingleDayHoliday[]> {
    if (!stateCode) return [];
    
    try {
      const yearHolidays = getHolidaysForYear(year, stateCode).public;
      
      return yearHolidays
        .map(holiday => {
          const date = parseDateString(holiday.start);
          
          if (!isValidDate(date)) {
            console.error('Invalid public holiday date:', holiday);
            return null;
          }
          
          const holidayObj: SingleDayHoliday = {
            name: holiday.name,
            date: holiday.start,
            type: 'public',
            state: stateCode,
            nationwide: holiday.type === 'public'
          };
          
          return holidayObj;
        })
        .filter((h): h is SingleDayHoliday => h !== null);
    } catch (error) {
      console.error('Error getting public holidays:', error);
      return [];
    }
  }
};